import { useCallback, useEffect, useRef, useState } from 'react';
import { useLexAiWebSocket, buildLexAiWebSocketUrl } from './useLexAiWebSocket';
import type {
  ChatMessage,
  ChatMessageChunk,
  ChatOutboundPayload,
} from '../_types';
import { CHAT_MAX_MESSAGE_LENGTH } from '../_types';
import { ReadyState } from 'react-use-websocket';

type ChatFlowStatus = 'connecting' | 'idle' | 'sending' | 'streaming' | 'error';

function parseChunk(data: object | null): ChatMessageChunk | null {
  if (!data || typeof data !== 'object') return null;
  const o = data as Record<string, unknown>;
  const type = o.type as string | undefined;
  const text = typeof o.text === 'string' ? o.text : '';
  if (!type) return null;
  return {
    type: type as ChatMessageChunk['type'],
    text,
    sessionId: typeof o.sessionId === 'string' ? o.sessionId : undefined,
    awsRequestId: typeof o.awsRequestId === 'string' ? o.awsRequestId : undefined,
  };
}

function nextId(): string {
  return `msg-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export type UseLexAiChatFlowResult = {
  messages: ChatMessage[];
  partialInbound: string;
  status: ChatFlowStatus;
  connectionError: string | null;
  sessionId: string | null;
  send: (text: string) => void;
  clear: () => void;
  stop: () => void;
  canSend: boolean;
  validationError: string | null;
};

/**
 * Chat flow state and WebSocket handling for Lex AI.
 * Connects when shouldConnect is true; parses inbound chunks and updates messages/partialInbound/sessionId.
 */
export function useLexAiChatFlow(shouldConnect: boolean): UseLexAiChatFlowResult {
  const getUrl = useCallback(() => (shouldConnect ? buildLexAiWebSocketUrl() : null), [shouldConnect]);
  const { sendJson, lastJsonMessage, readyState } = useLexAiWebSocket(getUrl);

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [partialInbound, setPartialInbound] = useState('');
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [status, setStatus] = useState<ChatFlowStatus>('connecting');
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const lastAwsRequestId = useRef<string | null>(null);
  const streamTurnNumber = useRef(0);
  const stopped = useRef(false);
  const partialInboundRef = useRef('');
  partialInboundRef.current = partialInbound;

  const isOpen = readyState === ReadyState.OPEN;
  const isConnecting = readyState === ReadyState.CONNECTING;

  useEffect(() => {
    if (readyState === ReadyState.CLOSED && shouldConnect) {
      setConnectionError('Connection closed');
      setStatus('error');
    } else if (isOpen && status === 'connecting') {
      setConnectionError(null);
      setStatus('idle');
    }
  }, [isOpen, readyState, shouldConnect, status]);

  useEffect(() => {
    const chunk = parseChunk(lastJsonMessage);
    if (!chunk) return;

    if (chunk.type === 'ERROR') {
      setStatus('error');
      setPartialInbound('');
      return;
    }

    if (chunk.sessionId) setSessionId(chunk.sessionId);
    if (chunk.awsRequestId) lastAwsRequestId.current = chunk.awsRequestId;

    if (chunk.type === 'INIT') {
      setPartialInbound('');
      setStatus('streaming');
      stopped.current = false;
      return;
    }

    if (chunk.type === 'PART') {
      if (!stopped.current) setPartialInbound((prev) => prev + chunk.text);
      return;
    }

    if (chunk.type === 'END') {
      const textToCommit = partialInboundRef.current;
      if (textToCommit && !stopped.current) {
        streamTurnNumber.current += 1;
        setMessages((m) => [
          ...m,
          {
            id: nextId(),
            role: 'assistant',
            text: textToCommit,
            awsRequestId: lastAwsRequestId.current ?? undefined,
          },
        ]);
      }
      setPartialInbound('');
      setStatus('idle');
      stopped.current = false;
    }
  }, [lastJsonMessage]);

  const send = useCallback(
    (text: string) => {
      setValidationError(null);
      const trimmed = text.trim();
      if (!trimmed) return;
      if (trimmed.length > CHAT_MAX_MESSAGE_LENGTH) {
        setValidationError(`Message must be ${CHAT_MAX_MESSAGE_LENGTH} characters or fewer.`);
        return;
      }
      if (!isOpen) {
        setConnectionError('Not connected');
        setStatus('error');
        return;
      }

      const userMsg: ChatMessage = {
        id: nextId(),
        role: 'user',
        text: trimmed,
      };
      setMessages((m) => [...m, userMsg]);
      setStatus('sending');

      const payload: ChatOutboundPayload = {
        action: 'chatbot',
        message: trimmed,
        route: 'chatbot',
      };
      if (sessionId) payload.sessionId = sessionId;
      sendJson(payload);
      setStatus('streaming');
    },
    [isOpen, sessionId, sendJson]
  );

  const clear = useCallback(() => {
    setMessages([]);
    setPartialInbound('');
    setSessionId(null);
    setConnectionError(null);
    setValidationError(null);
    setStatus(isOpen ? 'idle' : 'connecting');
    streamTurnNumber.current = 0;
    stopped.current = false;
  }, [isOpen]);

  const stop = useCallback(() => {
    stopped.current = true;
    const textToCommit = partialInboundRef.current;
    if (textToCommit) {
      streamTurnNumber.current += 1;
      setMessages((m) => [
        ...m,
        {
          id: nextId(),
          role: 'assistant',
          text: textToCommit,
          awsRequestId: lastAwsRequestId.current ?? undefined,
        },
      ]);
    }
    setPartialInbound('');
    setStatus('idle');
  }, []);

  const canSend = isOpen && status !== 'sending';

  return {
    messages,
    partialInbound,
    status,
    connectionError,
    sessionId,
    send,
    clear,
    stop,
    canSend,
    validationError,
  };
}
